// ============================================================
//  Page /sessions/:id — éditeur de session
//  - Métadonnées (nom, dates, mot secret)
//  - Boutons Démarrer / Terminer
//  - Liste des groupes + bouton ajouter / supprimer
// ============================================================

import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as api from "../lib/api";
import {
  NIVEAUX,
  type Groupe,
  type GroupeInput,
  type Parcours,
  type Session,
  type SessionStatut,
} from "../lib/types";

const STATUT_LABEL: Record<SessionStatut, { text: string; cls: string }> = {
  preparee: { text: "Préparée", cls: "stat-prep" },
  en_cours: { text: "● En cours", cls: "stat-active" },
  terminee: { text: "Terminée", cls: "stat-done" },
};

export function SessionEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [session, setSession] = useState<Session | null>(null);
  const [groupes, setGroupes] = useState<Groupe[]>([]);
  const [parcours, setParcours] = useState<Parcours[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showGroupeForm, setShowGroupeForm] = useState(false);
  const [actionPending, setActionPending] = useState(false);

  async function reload() {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const { session: s, groupes: gs } = await api.getSessionWithGroupes(id);
      setSession(s);
      setGroupes(gs);
      const ps = await api.listParcours(s.lieu_id);
      setParcours(ps);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  async function onStart() {
    if (!session) return;
    setActionPending(true);
    try {
      const s = await api.startSession(session.id);
      setSession(s);
    } catch (e) {
      alert("Erreur : " + (e as Error).message);
    } finally {
      setActionPending(false);
    }
  }

  async function onEnd() {
    if (!session) return;
    if (!confirm("Terminer la session ? Les équipes ne pourront plus jouer.")) return;
    setActionPending(true);
    try {
      const s = await api.endSession(session.id);
      setSession(s);
    } catch (e) {
      alert("Erreur : " + (e as Error).message);
    } finally {
      setActionPending(false);
    }
  }

  async function onCreateGroupe(input: GroupeInput) {
    if (!session) return;
    await api.createGroupe(session.id, input);
    setShowGroupeForm(false);
    await reload();
  }

  async function onDeleteGroupe(g: Groupe) {
    if (!confirm(`Supprimer l'équipe ${g.nom} (${g.code_acces}) ?`)) return;
    await api.deleteGroupe(g.id);
    setGroupes((prev) => prev.filter((x) => x.id !== g.id));
  }

  async function onResetGroupe(g: Groupe) {
    if (!confirm(`Reset les progressions du groupe ${g.nom} ?\nLes balises validées et les blocus seront effacés.`)) return;
    try {
      await api.resetGroupe(g.id);
      alert("Progressions réinitialisées ✅");
    } catch (e) {
      alert("Reset échoué : " + (e as Error).message);
    }
  }

  if (loading) return <div className="page-loading">Chargement…</div>;
  if (error) return <div className="page-error">Erreur : {error}</div>;
  if (!session) return <div className="page-error">Session introuvable</div>;

  const stat = STATUT_LABEL[session.statut];
  const canEdit = session.statut === "preparee";
  const canStart = session.statut === "preparee";
  const canEnd = session.statut === "en_cours";

  return (
    <div className="questions-page">
      <header className="questions-head">
        <div>
          <button onClick={() => navigate("/sessions")} className="btn-back">← Retour</button>
          <h1>{session.nom}</h1>
          <p className="questions-sub">
            <span className={`badge ${stat.cls}`}>{stat.text}</span>{" "}
            · Début {new Date(session.date_debut).toLocaleString("fr-FR")}{" "}
            {session.date_fin && <>· Fin {new Date(session.date_fin).toLocaleString("fr-FR")}</>}
          </p>
        </div>
        <div className="questions-actions">
          {session.statut === "en_cours" && (
            <button onClick={() => navigate(`/sessions/${session.id}/dashboard`)} className="btn-secondary">
              📊 Dashboard live
            </button>
          )}
          {canStart && (
            <button onClick={onStart} disabled={actionPending} className="btn-primary">
              ▶ Démarrer la session
            </button>
          )}
          {canEnd && (
            <button onClick={onEnd} disabled={actionPending} className="btn-danger-small">
              ⏹ Terminer
            </button>
          )}
        </div>
      </header>

      <div className="session-info">
        <div className="info-row">
          <strong>Mot secret de la base :</strong>{" "}
          <code className="mot-secret">{session.mot_secret_base}</code>
        </div>
      </div>

      <section className="groupes-section">
        <div className="section-head">
          <h2>Équipes ({groupes.length})</h2>
          {canEdit && (
            <button onClick={() => setShowGroupeForm(true)} className="btn-primary">
              + Ajouter une équipe
            </button>
          )}
        </div>

        {groupes.length === 0 ? (
          <p className="hint">Aucune équipe encore. Ajoutes-en une avant de démarrer la session.</p>
        ) : (
          <table className="questions-table">
            <thead>
              <tr>
                <th>Équipe (code)</th>
                <th>Nom</th>
                <th>Niveau</th>
                <th>Parcours</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {groupes.map((g) => {
                const p = parcours.find((x) => x.id === g.parcours_id);
                return (
                  <tr key={g.id}>
                    <td><code className="code-acces">{g.code_acces}</code></td>
                    <td>{g.nom}</td>
                    <td>{g.niveau && <span className="badge badge-niveau">{g.niveau}</span>}</td>
                    <td>{p?.nom ?? <span className="muted">supprimé</span>}</td>
                    <td className="cell-actions">
                      {canEdit && <button onClick={() => onResetGroupe(g)} title="Reset progressions">♻️</button>}
                      {canEdit && <button onClick={() => onDeleteGroupe(g)} title="Supprimer">🗑</button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      {showGroupeForm && (
        <GroupeForm
          parcours={parcours}
          onSubmit={onCreateGroupe}
          onCancel={() => setShowGroupeForm(false)}
        />
      )}
    </div>
  );
}

// ============================================================
//  Formulaire d'ajout de groupe
// ============================================================

function GroupeForm({
  parcours,
  onSubmit,
  onCancel,
}: {
  parcours: Parcours[];
  onSubmit: (input: GroupeInput) => Promise<void>;
  onCancel: () => void;
}) {
  const [nom, setNom] = useState("");
  const [niveau, setNiveau] = useState("");
  const [parcoursId, setParcoursId] = useState(parcours[0]?.id ?? "");
  const [codeEquipe, setCodeEquipe] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handle(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const trimmedCode = codeEquipe.trim();
      await onSubmit({
        nom: nom.trim(),
        niveau: niveau || null,
        parcours_id: parcoursId,
        ...(trimmedCode ? { code_acces: trimmedCode } : {}),
      });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  if (parcours.length === 0) {
    return (
      <div className="modal-backdrop" onClick={onCancel}>
        <div className="modal-form" onClick={(e) => e.stopPropagation()}>
          <h2>Aucun parcours disponible</h2>
          <p className="hint">
            Crée d'abord un parcours dans la section <strong>Parcours</strong>.
          </p>
          <div className="modal-actions">
            <button onClick={onCancel} className="btn-secondary">Fermer</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal-form" onClick={(e) => e.stopPropagation()}>
        <h2>Nouvelle équipe</h2>
        <p className="modal-hint">
          Le code de l'équipe est ce que les enfants taperont sur leur téléphone.
          Laisse vide pour qu'il soit généré (ex : VERT-CM1-01).
        </p>
        <form onSubmit={handle}>
          <label>
            <span>Nom de l'équipe *</span>
            <input
              type="text"
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              required
              autoFocus
              placeholder="Ex : Équipe Verte"
              maxLength={100}
            />
          </label>
          <label>
            <span>Code équipe <small>(laisse vide pour auto-générer)</small></span>
            <input
              type="text"
              value={codeEquipe}
              onChange={(e) => setCodeEquipe(e.target.value.toUpperCase())}
              placeholder="Ex : VERTS, EQUIPE-A, …"
              maxLength={40}
              pattern="[A-Z0-9\-]*"
              title="Lettres majuscules, chiffres et tirets uniquement"
            />
          </label>
          <div className="form-row">
            <label>
              <span>Niveau</span>
              <select value={niveau} onChange={(e) => setNiveau(e.target.value)}>
                <option value="">—</option>
                {NIVEAUX.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <label>
              <span>Parcours *</span>
              <select value={parcoursId} onChange={(e) => setParcoursId(e.target.value)} required>
                {parcours.map((p) => (
                  <option key={p.id} value={p.id}>{p.nom}{p.niveau ? ` (${p.niveau})` : ""}</option>
                ))}
              </select>
            </label>
          </div>
          {error && <p className="form-error">✗ {error}</p>}
          <div className="modal-actions">
            <button type="button" onClick={onCancel} className="btn-secondary">Annuler</button>
            <button type="submit" disabled={submitting} className="btn-primary">
              {submitting ? "Création…" : "Créer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
